import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  ClipboardList,
  MessageCircle,
  Sparkles,
  Star,
} from "lucide-react";
import { DFE_PDF_PATH, DFE_PRINT_PATH } from "../data/direccionFinancieraPaths";
import HorizontalList from "../components/horizontalList";
import { useCfoBundle } from "../context/CfoLanguageContext";
import { convertPriceStringToUsd, formatPlanPrice } from "../utils/cfoPriceFormat";

const journeyIcons = [ClipboardList, Briefcase, Sparkles];

const formatJourneyPrice = (price, lang) => {
  if (lang !== "en" || !price) return price;
  if (/personalizada|custom pricing|cotización/i.test(price)) return price;

  const separator = price.includes("–") ? "–" : price.includes("-") ? "-" : null;
  if (separator) {
    const parts = price.split(separator).map((part) => part.trim());
    return parts.map((part) => convertPriceStringToUsd(part)).join(` ${separator} `);
  }

  return convertPriceStringToUsd(price);
};

const formatJourneyPriceNote = (note, lang) => {
  if (lang !== "en" || !note) return note;
  const copMatches = note.match(/COP\s*\$[\d.,]+/gi);
  if (!copMatches) return note;
  return copMatches.reduce(
    (text, match) => text.replace(match, convertPriceStringToUsd(match)),
    note
  );
};

const normalizeFeaturedService = (item) => {
  if (typeof item === "string") {
    return { id: item, titulo: item, whatsapp: null };
  }
  return {
    id: item.id ?? item.titulo,
    titulo: item.titulo ?? String(item),
    whatsapp: item.whatsapp ?? null,
  };
};

const PLAN_COLUMNS = [
  { key: "essential", label: "Essential", highlight: false },
  { key: "growth", label: "Growth", highlight: true },
  { key: "strategic", label: "Strategic", highlight: false },
];

const CeldaComparativo = ({ valor, includedLabel, inline = false }) => {
  if (valor === "check") {
    return (
      <CheckCircle2
        className={`w-5 h-5 text-emerald-500 shrink-0 ${inline ? "" : "mx-auto"}`}
        aria-label={includedLabel}
      />
    );
  }
  if (valor === "dash") {
    return <span className="text-slate-400 font-medium">—</span>;
  }
  return <span className={`font-medium ${inline ? "text-sm text-right" : "text-sm"}`}>{valor}</span>;
};

function CfoFraccionalPlanes() {
  const { cfo, lang } = useCfoBundle();
  const p = cfo.planes;
  const j = p.journey;

  const journeyCtaHref = (phase) => {
    if (phase.ctaType === "anchor") return phase.ctaHref;
    if (phase.ctaType === "whatsapp-specialized") return cfo.whatsapp.serviciosEspecializados;
    return cfo.whatsapp.diagnostico;
  };

  const journeyCtaExternal = (phase) => phase.ctaType?.startsWith("whatsapp");

  const d = useMemo(() => {
    const base =
      lang !== "en"
        ? cfo.data
        : {
            ...cfo.data,
            comparativoPlanes: cfo.data.comparativoPlanes.map((row) => ({
              ...row,
              essential: convertPriceStringToUsd(row.essential),
              growth: convertPriceStringToUsd(row.growth),
              strategic: convertPriceStringToUsd(row.strategic),
            })),
            serviciosAdicionales: cfo.data.serviciosAdicionales.map((servicio) => ({
              ...servicio,
              inversion: convertPriceStringToUsd(servicio.inversion),
            })),
          };

    return {
      ...base,
      serviciosDestacados: (base.serviciosDestacados ?? []).map(normalizeFeaturedService),
      exclusiones: (base.exclusiones ?? []).filter((item) => typeof item === "string"),
    };
  }, [cfo.data, lang]);

  return (
    <div className="bg-slate-50 min-w-0">
      <section className="bg-[#2c4497] text-white py-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/empresas/direccion-de-innovacion-financiera"
            className="inline-flex items-center gap-2 text-white/90 text-sm font-semibold hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {p.backLink}
          </Link>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.35em] text-white/80 mb-3">
            {p.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold">{p.title}</h1>
          <p className="mt-4 text-white/90 max-w-2xl">{p.subtitle}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={DFE_PDF_PATH}
              download
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-bold text-[#123a72] hover:bg-[#e8f2ff] transition"
            >
              {p.downloadPdf}
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={DFE_PRINT_PATH}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-6 py-2.5 text-sm font-bold text-white hover:bg-white/10 transition"
            >
              {lang === "en" ? "Printable version" : "Versión imprimible"}
            </a>
          </div>
        </div>
      </section>

      <HorizontalList items={p.nav} even plain navClassName="bg-[#0A1931]" darkNav />

      <section id="ruta" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-gradient-to-b from-[#e8f2f4] to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{j.label}</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-[#0b1f3f] leading-snug">{j.title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{j.subtitle}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {j.phases.map((phase, index) => {
              const Icon = journeyIcons[index];
              const isRecommended = phase.recommended;
              const isSuggested = phase.suggested;
              const isHighlighted = isRecommended || isSuggested;
              const ctaHref = journeyCtaHref(phase);
              const ctaExternal = journeyCtaExternal(phase);
              const CtaTag = ctaExternal ? "a" : "a";
              const ctaProps = ctaExternal
                ? { href: ctaHref, target: "_blank", rel: "noopener noreferrer" }
                : { href: ctaHref };

              return (
                <article
                  key={phase.phase}
                  className={`relative flex flex-col rounded-3xl border bg-white p-6 sm:p-8 shadow-md transition hover:shadow-xl ${
                    isHighlighted
                      ? "border-[#1746a2] ring-2 ring-[#1746a2]/20 lg:-translate-y-1"
                      : "border-slate-200"
                  }`}
                >
                  {isRecommended && (
                    <span className="absolute -top-3 left-6 rounded-full bg-[#1746a2] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      {j.recommended}
                    </span>
                  )}
                  {isSuggested && (
                    <span className="absolute -top-3 left-6 rounded-full bg-[#276ae8] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      {j.suggestedPlan}: {phase.suggestedPlanName}
                    </span>
                  )}

                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
                        isHighlighted ? "bg-[#1746a2] text-white" : "bg-[#1746a2]/10 text-[#1746a2]"
                      }`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">{phase.phase}</p>
                      <h3 className="text-xl font-extrabold text-[#0b1f3f] leading-tight">{phase.title}</h3>
                    </div>
                  </div>

                  {phase.summary && (
                    <p className="mb-5 text-sm text-slate-600 leading-relaxed">{phase.summary}</p>
                  )}

                  <div className="mb-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{j.investmentLabel}</p>
                    <p className="mt-2 text-2xl font-extrabold text-[#1746a2]">
                      {formatJourneyPrice(phase.price, lang)}
                    </p>
                    {phase.priceNote && (
                      <p className="mt-1 text-sm text-slate-500">{formatJourneyPriceNote(phase.priceNote, lang)}</p>
                    )}
                    {isSuggested && phase.suggestedPlanPrice && (
                      <p className="mt-2 text-sm font-semibold text-[#276ae8]">
                        {phase.suggestedPlanName}: {formatPlanPrice(phase.suggestedPlanPrice, lang)}
                        {lang === "es" ? " / mes" : " / mo"}
                      </p>
                    )}
                    {lang === "en" && p.usdPriceNote && phase.price !== "Custom pricing" && (
                      <p className="mt-1 text-xs text-slate-500 leading-snug">{p.usdPriceNote}</p>
                    )}
                  </div>

                  {phase.deliverables?.length > 0 && (
                    <div className="mb-5">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                        {j.deliverablesLabel}
                      </p>
                      <ul className="space-y-2">
                        {phase.deliverables.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="h-4 w-4 text-[#1746a2] shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {phase.description && (
                    <p className="mb-5 text-sm text-slate-600 leading-relaxed flex-1">{phase.description}</p>
                  )}

                  {phase.bridge && (
                    <p className="mb-5 rounded-2xl bg-[#1746a2]/5 border border-[#1746a2]/15 px-4 py-3 text-sm text-slate-700 leading-relaxed">
                      {phase.bridge}
                    </p>
                  )}

                  <div className="mt-auto pt-2">
                    <CtaTag
                      {...ctaProps}
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition ${
                        isHighlighted
                          ? "bg-[#1746a2] text-white hover:bg-[#123a72]"
                          : "border-2 border-[#1746a2] text-[#1746a2] hover:bg-[#1746a2]/5"
                      }`}
                    >
                      {phase.cta}
                      <ArrowRight className="h-4 w-4" />
                    </CtaTag>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="comparativo" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0b1f3f] text-center mb-10">{p.comparativoTitle}</h2>

          <div className="lg:hidden space-y-3">
            {d.comparativoPlanes.map((fila, index) => (
              <article
                key={fila.caracteristica}
                className={`rounded-2xl border border-slate-200 p-4 shadow-sm ${
                  index % 2 === 0 ? "bg-slate-50" : "bg-white"
                }`}
              >
                <h3 className="text-sm font-bold text-[#0b1f3f] leading-snug">{fila.caracteristica}</h3>
                <div className="mt-3 space-y-2">
                  {PLAN_COLUMNS.map(({ key, label, highlight }) => (
                    <div
                      key={key}
                      className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 ${
                        highlight ? "bg-[#e8f2ff]/70 border border-[#1746a2]/15" : "bg-white/80"
                      }`}
                    >
                      <span className="text-xs font-bold uppercase tracking-wide text-[#1746a2]">{label}</span>
                      <CeldaComparativo valor={fila[key]} includedLabel={p.includedAria} inline />
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="hidden lg:block rounded-3xl bg-white overflow-hidden shadow-2xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1746a2] text-white">
                  <th className="text-left px-5 py-4 font-bold">{p.tableFeature}</th>
                  <th className="px-4 py-4 font-bold text-center">Essential</th>
                  <th className="px-4 py-4 font-bold text-center bg-[#123a72]">Growth</th>
                  <th className="px-4 py-4 font-bold text-center">Strategic</th>
                </tr>
              </thead>
              <tbody>
                {d.comparativoPlanes.map((fila, index) => (
                  <tr key={fila.caracteristica} className={index % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-5 py-3.5 text-[#0b1f3f] font-medium border-b border-slate-100">
                      {fila.caracteristica}
                    </td>
                    <td className="px-4 py-3.5 text-center text-slate-700 border-b border-slate-100">
                      <CeldaComparativo valor={fila.essential} includedLabel={p.includedAria} />
                    </td>
                    <td className="px-4 py-3.5 text-center text-slate-700 border-b border-slate-100 bg-[#e8f2ff]/40">
                      <CeldaComparativo valor={fila.growth} includedLabel={p.includedAria} />
                    </td>
                    <td className="px-4 py-3.5 text-center text-slate-700 border-b border-slate-100">
                      <CeldaComparativo valor={fila.strategic} includedLabel={p.includedAria} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 text-center">
            <a
              href={cfo.whatsapp.cfo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-8 py-3 text-white font-bold hover:bg-[#123a72] transition"
            >
              {p.ctaWhatsapp}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section id="detalle-planes" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {d.planes.map((plan) => (
              <article
                key={plan.nombre}
                className={`rounded-3xl flex flex-col border ${
                  plan.destacado
                    ? "border-[#276ae8] ring-4 ring-[#276ae8]/30 shadow-xl bg-white"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="p-8 text-[#0b1f3f]">
                  {plan.destacado && (
                    <span className="inline-block mb-3 rounded-full bg-[#1746a2] px-3 py-1 text-xs font-bold text-white uppercase">
                      {p.recommended}
                    </span>
                  )}
                  <h3 className="text-2xl font-extrabold">
                    {p.planPrefix} {plan.nombre}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{plan.descripcion}</p>
                  <p className="mt-5 text-xs uppercase tracking-wide text-slate-500">{p.monthlyInvestment}</p>
                  <p className="text-3xl font-extrabold text-[#1746a2]">{formatPlanPrice(plan.precio, lang)}</p>
                  {lang === "en" && p.usdPriceNote && (
                    <p className="mt-1 text-xs text-slate-500 leading-snug">{p.usdPriceNote}</p>
                  )}
                </div>
                <div className="flex-1 px-8 pb-8 text-slate-700">
                  <p className="text-sm font-bold uppercase tracking-wide mb-4 text-slate-500">{p.includes}</p>
                  <ul className="space-y-3">
                    {plan.incluye.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-[#1746a2]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href={cfo.whatsapp.cfo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-8 py-3 text-white font-bold hover:bg-[#123a72] transition"
            >
              {p.ctaWhatsapp}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section id="no-incluidos" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#e8f2f4]">
        <div className="max-w-6xl mx-auto rounded-3xl border border-[#1746a2]/20 p-8 sm:p-10 bg-white shadow-sm">
          <h2 className="text-2xl font-extrabold text-[#0b1f3f] mb-2">{p.notIncludedTitle}</h2>
          <p className="text-slate-600 mb-2 leading-relaxed">{p.notIncludedP1}</p>
          <p className="text-slate-600 mb-8 leading-relaxed">{p.notIncludedP2}</p>

          {d.serviciosDestacados?.length > 0 && (
            <div className="mb-8">
              <p className="text-sm font-bold uppercase tracking-wide text-emerald-700 mb-3">
                {p.featuredServicesLabel}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {d.serviciosDestacados.map((item) => (
                  <li
                    key={item.id}
                    className="flex flex-col rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-emerald-900 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <Star
                        className="w-5 h-5 text-emerald-600 fill-emerald-400 shrink-0 mt-0.5"
                        aria-hidden
                      />
                      <span className="font-medium leading-snug">{item.titulo}</span>
                    </div>
                    <a
                      href={item.whatsapp || cfo.whatsapp.serviciosSolicitud}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1da851] transition"
                    >
                      {p.featuredCta}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {d.exclusiones.map((item, index) => (
              <li
                key={`${item}-${index}`}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700"
              >
                <MessageCircle className="w-5 h-5 text-[#1746a2] shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <a
            href={cfo.whatsapp.serviciosSolicitud}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#1746a2] px-8 py-3 text-white font-bold hover:bg-[#123a72] transition"
          >
            {p.requestWhatsapp}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <section id="adicionales" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2] mb-2">
              {p.additionalLabel}
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0b1f3f] mb-4">{p.specializedTitle}</h2>
            <p className="text-slate-600 max-w-3xl leading-relaxed">{d.serviciosEspecializadosIntro}</p>
          </div>

          <div className="lg:hidden space-y-4">
            {d.serviciosAdicionales.map(({ servicio, descripcion, entregables, inversion }, index) => (
              <article
                key={servicio}
                className={`rounded-2xl border border-slate-200 p-5 shadow-sm ${
                  index % 2 === 0 ? "bg-slate-50" : "bg-white"
                }`}
              >
                <h3 className="text-base font-extrabold text-[#0b1f3f] leading-snug">{servicio}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{descripcion}</p>
                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
                    {p.tableDeliverables}
                  </p>
                  <ul className="space-y-2">
                    {entregables.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-[#1746a2] shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-4 rounded-xl bg-[#1746a2]/5 border border-[#1746a2]/15 px-3 py-2 text-sm">
                  <span className="font-bold text-[#0b1f3f]">{p.tableInvestment}: </span>
                  <span className="font-semibold text-[#1746a2]">{inversion}</span>
                </p>
              </article>
            ))}
          </div>

          <div className="hidden lg:block rounded-3xl border border-[#1746a2]/20 overflow-hidden shadow-sm">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="bg-[#1746a2] text-white">
                  <th className="text-left px-5 py-4 font-bold w-[18%]">{p.tableService}</th>
                  <th className="text-left px-5 py-4 font-bold w-[22%]">{p.tableDescription}</th>
                  <th className="text-left px-5 py-4 font-bold w-[38%]">{p.tableDeliverables}</th>
                  <th className="text-left px-5 py-4 font-bold w-[22%]">{p.tableInvestment}</th>
                </tr>
              </thead>
              <tbody>
                {d.serviciosAdicionales.map(({ servicio, descripcion, entregables, inversion }, index) => (
                  <tr key={servicio} className={index % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-5 py-4 font-semibold text-[#0b1f3f] border-b border-slate-100 align-top">
                      {servicio}
                    </td>
                    <td className="px-5 py-4 text-slate-600 border-b border-slate-100 align-top leading-relaxed">
                      {descripcion}
                    </td>
                    <td className="px-5 py-4 text-slate-600 border-b border-slate-100 align-top leading-relaxed">
                      <ul className="space-y-1.5 list-disc list-inside text-sm">
                        {entregables.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-5 py-4 font-semibold text-[#1746a2] border-b border-slate-100 align-top">
                      {inversion}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs sm:text-sm text-slate-500 leading-relaxed">
            *{d.serviciosEspecializadosNota}
          </p>

          <a
            href={cfo.whatsapp.serviciosEspecializados}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#1746a2] px-8 py-3 text-white font-bold hover:bg-[#123a72] transition"
          >
            {p.ctaWhatsapp}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <section id="condiciones" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#0b1f3f]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-extrabold text-white mb-6">{p.conditionsTitle}</h2>
          <ul className="space-y-3 mb-10">
            {d.condicionesGenerales.map((item) => (
              <li key={item} className="flex items-start gap-3 text-slate-200">
                <CheckCircle2 className="w-5 h-5 text-[#9ec5ff] shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
          <a
            href={cfo.whatsapp.cfo}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-white px-8 py-3 text-[#123a72] font-bold hover:bg-[#e8f2ff] transition"
          >
            {p.ctaWhatsapp}
          </a>
        </div>
      </section>
    </div>
  );
}

export default CfoFraccionalPlanes;
